import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ProjectFilter } from '@/components/ProjectFilter';
import { PageError } from '@/components/PageError';
import { RecordCollectionModal } from '@/pages/cashflow/RecordCollectionModal';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface OverdueTrip {
  source: string;
  trip_id: string; trip_code: string;
  payer_name: string | null;
  rate_sar: string; amount_paid: string; outstanding: string;
  payment_due_date: string | null;
  payment_state: string;
  collector_user_id: string | null; collector_name: string | null;
  project_id: string | null; project_code: string | null;
  days_overdue: number | null;
}

const fmtSar = (s: string) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

function tone(days: number | null): string {
  if (days == null) return 'bg-muted text-muted-foreground';
  if (days <= 7) return 'bg-amber-500/15 text-amber-700 dark:text-amber-400';
  if (days <= 30) return 'bg-orange-500/15 text-orange-700 dark:text-orange-400';
  return 'bg-rose-500/15 text-rose-700 dark:text-rose-400';
}

export default function OverdueCollectionsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState<OverdueTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [myQueueOnly, setMyQueueOnly] = useState(false);
  const [collectFor, setCollectFor] = useState<{ flatbedTripId: string } | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (projectId)             params.set('projectId', projectId);
      if (myQueueOnly && user?.id) params.set('collectorUserId', user.id);
      const r = await apiClient.get<OverdueTrip[]>('/api/v1/payment-collections/overdue?' + params.toString());
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [projectId, myQueueOnly, user?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Clock className="h-4 w-4" /> {t('overdue.title', { defaultValue: 'Overdue collections' })}</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <ProjectFilter value={projectId} onChange={setProjectId} storageKey="overdue.projectId" />
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={myQueueOnly} onChange={(e) => setMyQueueOnly(e.target.checked)} className="h-4 w-4" />
            My queue only
          </label>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <PageError error={error} retry={refresh} />
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No overdue trips.</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left px-3 py-2">Trip</th>
                  <th className="text-left px-3 py-2">Payer</th>
                  <th className="text-right px-3 py-2">Outstanding</th>
                  <th className="text-left px-3 py-2">Due</th>
                  <th className="text-left px-3 py-2">Days over</th>
                  <th className="text-left px-3 py-2">Collector</th>
                  <th className="text-left px-3 py-2">Project</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.trip_id} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/dashboard/flatbed/${it.trip_id}`} className="hover:underline">{it.trip_code}</Link>
                    </td>
                    <td className="px-3 py-2 text-xs">{it.payer_name ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtSar(it.outstanding)}</td>
                    <td className="px-3 py-2 text-xs">{it.payment_due_date ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={'inline-flex items-center px-2 py-0.5 rounded text-[10px] ' + tone(it.days_overdue)}>{it.days_overdue ?? '—'} days</span>
                    </td>
                    <td className="px-3 py-2 text-xs">{it.collector_name ?? '—'}</td>
                    <td className="px-3 py-2 text-xs font-mono">{it.project_code ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setCollectFor({ flatbedTripId: it.trip_id })}>Record payment</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <RecordCollectionModal
        open={!!collectFor}
        onOpenChange={(o) => !o && setCollectFor(null)}
        flatbedTripId={collectFor?.flatbedTripId}
        onRecorded={() => { setCollectFor(null); refresh(); }}
      />
    </div>
  );
}
